/**
 * THE FOUR ROLES A CHART END DATE CAN BE SET FOR, and the fifth that cannot.
 *
 * <h2>⚠ THESE STRINGS ARE A WIRE CONTRACT</h2>
 *
 * They are the keys of `PUT /api/chart-window/settings`, the keys of the JSON document in the
 * bucket, and the values stored on `users/{uid}.role`. Java cannot hand a string to JavaScript,
 * so `chartEndDates.test.js` reads `UserRole.java` off disk and pins all five — the same guard
 * `ExitReason` and `Market` already carry, and for the same reason: a rename on one side is
 * invisible to the other until a date silently applies to nobody.
 *
 * <p>`integralDelta` is lower-case i and capital D, as the product is written everywhere else.
 * The server accepts any casing on read; this is what it should be WRITTEN as.
 *
 * <h2>Why Admin is named here at all, when it has no field</h2>
 *
 * So the dialog can say why there is no field for it. An absent control reads as a form somebody
 * did not finish unless something states the decision.
 */

/** The role an administrator holds. Has no end date, by design. */
export const ADMIN_ROLE = 'Admin';

/**
 * The four configurable roles, in the order the dialog shows them.
 *
 * <p>Paired per product — the paying audience then the trial for each — because that is the
 * comparison somebody setting these is making. Grouped by trial instead would put the two
 * products' trials side by side, which is not a question anybody asks.
 */
export const CHART_END_DATE_ROLES = Object.freeze([
  {
    role: 'Astro-prod',
    label: 'AstroStockCharts — paying',
    blurb: 'Customers on AstroStockCharts.Com with a live subscription.',
  },
  {
    role: 'Astro-trial',
    label: 'AstroStockCharts — trial',
    blurb: 'Trial accounts on AstroStockCharts.Com. These are already served the sample symbol.',
  },
  {
    role: 'integralDelta-prod',
    label: 'integralDelta — paying',
    blurb: 'Customers on integralDelta.ai with a live subscription.',
  },
  {
    role: 'integralDelta-trial',
    label: 'integralDelta — trial',
    blurb: 'Trial accounts on integralDelta.ai.',
  },
]);

/** Just the role strings, for a caller that does not need the copy. */
export const CHART_END_DATE_ROLE_KEYS = CHART_END_DATE_ROLES.map((r) => r.role);

/**
 * Whether a string is a date the server will accept.
 *
 * <p>The SHAPE and the CALENDAR both, because `2026-02-30` passes a regex and is not a date —
 * and a date the server rejects is a 400 the reader has to map back onto one of four fields
 * themselves. Checked here so the field names itself.
 */
export function isIsoDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return false;
  const s = value.trim();
  // UTC, never the local constructor: `new Date('2026-03-01')` is already UTC, but
  // `new Date(2026, 2, 1)` is local and a reader west of UTC would see the round trip shift a
  // day. The same rule every date comparison in this codebase follows.
  const t = Date.parse(`${s}T00:00:00Z`);
  if (Number.isNaN(t)) return false;
  return new Date(t).toISOString().slice(0, 10) === s;
}

/**
 * Today, as the chart's own calendar sees it.
 *
 * <p>LOCAL, deliberately, and it is the same call the session window makes: this feeds a native
 * date control and is shown beside one, both of which are local-calendar surfaces. An operator
 * west of UTC late in the evening would otherwise be told today is tomorrow.
 */
export function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * How many weekday columns the axis must reach to show `endDate`, given the last real bar.
 *
 * <p>Zero when the date is absent, unparseable, or not after the last bar — which is every
 * ordinary case, so the caller keeps its own default padding and nothing changes.
 *
 * <h2>WEEKDAYS, not calendar days</h2>
 *
 * The axis is one column per trading day and `extendBarData` pads with weekdays, so a calendar
 * count would overshoot by two days a week — about 40% — and leave the configured date well
 * short of the right-hand edge. This counts the same thing the padding creates.
 *
 * <p>No holiday calendar, deliberately: the padded columns are synthetic anyway, so the only
 * cost of counting a holiday as a column is one extra empty column per holiday. Reaching the
 * date is what matters; reaching it exactly is not knowable without an exchange calendar the
 * browser does not have.
 */
export function weekdaysBetween(lastBarDate, endDate) {
  if (!isIsoDate(String(lastBarDate || '').slice(0, 10)) || !isIsoDate(String(endDate || ''))) {
    return 0;
  }
  let t = Date.parse(`${String(lastBarDate).slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!(end > t)) return 0;

  let n = 0;
  const DAY = 86400000;
  // A hard ceiling, because this walks a day at a time and an end date typed as 9999 would
  // otherwise spin through three million iterations and pad an axis nobody can read. Ten years
  // of weekdays is far past any legitimate setting and still finite.
  const MAX = 2610;
  while (t < end && n < MAX) {
    t += DAY;
    const day = new Date(t).getUTCDay();
    if (day !== 0 && day !== 6) n += 1;
  }
  return n;
}
